import React from 'react';
import {
  IonItem,
  IonAvatar,
  IonLabel,
  IonIcon,
  IonNote,
} from '@ionic/react';
import { call, logoWhatsapp, locationOutline } from 'ionicons/icons';
import MemberStatusBadge from './MemberStatusBadge';
import { MEMBERSHIP_TYPE_LABELS } from '@/utils/labels';
import { formatEffectiveAddress } from '@/utils/address';
import type { Member } from '@/types/member';

interface MemberCardProps {
  member: Member;
  onClick?: () => void;
}

const MemberCard: React.FC<MemberCardProps> = ({ member, onClick }) => {
  const initials =
    (member.first_name[0] ?? '') + (member.last_name[0] ?? '');

  const phone = member.cell_phone ?? member.home_phone;
  const address = formatEffectiveAddress(member);

  const handlePhoneClick = (
    e: React.MouseEvent,
    phoneNum: string,
    type: 'call' | 'whatsapp',
  ) => {
    e.stopPropagation();
    if (type === 'whatsapp') {
      const digits = phoneNum.replace(/\D/g, '');
      const waNumber = digits.startsWith('0') ? '40' + digits.slice(1) : digits;
      window.open(`https://wa.me/${waNumber}`, '_blank');
    } else {
      window.open(`tel:${phoneNum}`, '_self');
    }
  };

  return (
    <IonItem
      button
      detail
      onClick={onClick}
      lines="full"
      style={{ '--padding-start': '12px' }}
    >
      <IonAvatar slot="start" style={{ width: '44px', height: '44px' }}>
        {member.photo_url ? (
          <img
            src={member.photo_url}
            alt={member.full_name}
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: 'var(--ion-color-primary)',
              color: 'var(--ion-color-primary-contrast)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            {initials.toUpperCase()}
          </div>
        )}
      </IonAvatar>

      <IonLabel>
        <h2 style={{ fontWeight: 600, fontSize: '15px' }}>
          {member.full_name}
        </h2>
        <p style={{ fontSize: '13px' }}>
          {member.sector_name && (
            <span style={{ marginRight: '8px' }}>{member.sector_name}</span>
          )}
          <MemberStatusBadge status={member.church_status} />
          {member.is_approved === false && (
            <span style={{
              marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '1px 6px',
              borderRadius: 4, color: '#b45309', background: '#fef3c7',
            }}>
              Neaprobat
            </span>
          )}
        </p>
        {address && (
          <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IonIcon icon={locationOutline} style={{ fontSize: '13px', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{address}</span>
          </p>
        )}
      </IonLabel>

      <div
        slot="end"
        style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
      >
        {phone && (
          <IonIcon
            icon={call}
            color="primary"
            style={{ fontSize: '20px', cursor: 'pointer' }}
            onClick={(e: React.MouseEvent) =>
              handlePhoneClick(e, phone, 'call')
            }
          />
        )}
        {phone && (
          <IonIcon
            icon={logoWhatsapp}
            color="success"
            style={{ fontSize: '20px', cursor: 'pointer' }}
            onClick={(e: React.MouseEvent) =>
              handlePhoneClick(e, phone, 'whatsapp')
            }
          />
        )}
        {member.membership_type && (
          <IonNote style={{ fontSize: '11px' }}>
            {MEMBERSHIP_TYPE_LABELS[member.membership_type] ?? member.membership_type}
          </IonNote>
        )}
      </div>
    </IonItem>
  );
};

export default MemberCard;
